import { format } from 'date-fns';
import { BriefcaseIcon, TargetIcon, DollarSignIcon, TrophyIcon } from 'lucide-react';
import type { Customer, EmploymentHistory, Achievement } from '@repo/types';
import { SectionCard, FieldTile, TagList, EmptyState, TimelineItem, ToneBadge, DescriptionList } from './_shared';
import { formatWorkMode, humanize, getAvailabilityStatusTone, getCurrentRole } from './utils';

const fmtDate = (v?: string | null) => (v ? format(new Date(v), 'MMM yyyy') : null);

/**
 * Professional background, work history and job preferences — limited to what
 * customers provide via the web app's Profile (Experience & Training) and
 * Preferences settings pages.
 */
export function CustomerProfessionalTab({ customer }: { customer: Customer }) {
  const currentRole = getCurrentRole(customer);
  const snapshot = ([
    ['Years of Experience', customer.yearsOfExperience != null ? `${customer.yearsOfExperience} years` : null],
    ['Current Title', currentRole.title],
    ['Current Company', currentRole.company],
    ['Industry', customer.currentIndustry],
    ['Career Level', humanize(customer.careerLevel)],
  ] as Array<[string, string | null | undefined]>).filter(([, v]) => Boolean(v));

  const prefs = customer.jobPreferences;
  const hasPrefs =
    prefs?.preferred_job_roles?.length ||
    prefs?.industry_preferences?.length ||
    prefs?.preferred_work_type?.length ||
    prefs?.company_type_preferences?.length ||
    prefs?.preferred_experience?.length ||
    customer.preferredLocations?.length ||
    customer.preferredJobTypes?.length ||
    customer.preferredCategories?.length ||
    customer.relocationWilling !== undefined ||
    customer.remoteOnly !== undefined ||
    customer.comfortableTravelling !== undefined;

  return (
    <div className="space-y-5">
      {/* Snapshot */}
      <SectionCard icon={BriefcaseIcon} title="Professional Background">
        {snapshot.length > 0 || customer.availabilityStatus ? (
          <DescriptionList
            items={[
              ...snapshot.map(([label, value]) => ({ label, value })),
              {
                label: 'Availability',
                value: customer.availabilityStatus ? (
                  <ToneBadge tone={getAvailabilityStatusTone(customer.availabilityStatus)}>
                    {humanize(customer.availabilityStatus)}
                  </ToneBadge>
                ) : null,
              },
            ]}
          />
        ) : (
          <EmptyState icon={BriefcaseIcon} message="No professional background on file" />
        )}
      </SectionCard>

      {/* Work history */}
      <SectionCard
        icon={BriefcaseIcon}
        title="Work History"
        description={customer.employmentHistory?.length ? `${customer.employmentHistory.length} position(s)` : undefined}
      >
        {customer.employmentHistory?.length ? (
          <div className="space-y-3">
            {customer.employmentHistory.map((work: EmploymentHistory, idx) => (
              <TimelineItem
                key={idx}
                title={work.designation}
                subtitle={work.company_name}
                meta={work.start_date ? <span>{work.start_date} — {work.end_date || 'Present'}</span> : undefined}
              >
                {work.description ? (
                  <p className="text-xs text-muted-foreground">{work.description}</p>
                ) : null}
              </TimelineItem>
            ))}
          </div>
        ) : (
          <EmptyState icon={BriefcaseIcon} message="No work history available" />
        )}
      </SectionCard>

      {/* Job preferences */}
      <SectionCard icon={TargetIcon} title="Job Preferences">
        {hasPrefs ? (
          <div className="space-y-4">
            <TagList label="Preferred Roles" items={prefs?.preferred_job_roles ?? []} />
            <TagList label="Preferred Locations" items={customer.preferredLocations ?? []} />
            <TagList
              label="Work Modes"
              items={(prefs?.preferred_work_type ?? []).map((m) => formatWorkMode(m))}
            />
            <TagList label="Job Types" items={customer.preferredJobTypes ?? []} />
            <TagList label="Categories" items={customer.preferredCategories ?? []} />
            <TagList label="Industries" items={prefs?.industry_preferences ?? []} />
            <TagList label="Company Types" items={prefs?.company_type_preferences ?? []} />
            <TagList label="Experience Range" items={prefs?.preferred_experience ?? []} />
            {(customer.relocationWilling !== undefined ||
              customer.remoteOnly !== undefined ||
              customer.comfortableTravelling !== undefined) && (
              <div className="flex flex-wrap gap-2 pt-1">
                {customer.relocationWilling !== undefined && (
                  <ToneBadge tone={customer.relocationWilling ? 'success' : 'neutral'}>
                    {customer.relocationWilling ? 'Open to relocation' : 'No relocation'}
                  </ToneBadge>
                )}
                {customer.remoteOnly && <ToneBadge tone="info">Remote only</ToneBadge>}
                {customer.comfortableTravelling && <ToneBadge tone="info">Comfortable travelling</ToneBadge>}
              </div>
            )}
          </div>
        ) : (
          <EmptyState icon={TargetIcon} message="No job preferences set" />
        )}
      </SectionCard>

      {/* Compensation */}
      <SectionCard icon={DollarSignIcon} title="Compensation">
        {prefs?.expected_salary_range ? (
          <FieldTile label="Expected Salary Range" className="bg-primary/5">
            <span className="text-base text-primary">{prefs.expected_salary_range}</span>
          </FieldTile>
        ) : (
          <EmptyState icon={DollarSignIcon} message="No compensation info" />
        )}
      </SectionCard>

      {/* Achievements */}
      <SectionCard
        icon={TrophyIcon}
        title="Achievements"
        description={customer.achievements?.length ? `${customer.achievements.length} total` : undefined}
      >
        {customer.achievements?.length ? (
          <div className="space-y-3">
            {customer.achievements.map((achievement: Achievement, idx) => (
              <TimelineItem
                key={idx}
                title={achievement.title}
                subtitle={humanize(achievement.category)}
                meta={fmtDate(achievement.date) ? <span>{fmtDate(achievement.date)}</span> : undefined}
              >
                {achievement.description && (
                  <p className="text-xs text-muted-foreground">{achievement.description}</p>
                )}
              </TimelineItem>
            ))}
          </div>
        ) : (
          <EmptyState icon={TrophyIcon} message="No achievements recorded" />
        )}
      </SectionCard>
    </div>
  );
}
